import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import {
	Button,
	Table,
	Empty,
	Tag,
	Input,
	Tooltip,
	Select,
	Switch,
	InputNumber,
} from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faX } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
	updateFormAction,
	updateMessage,
} from "../../../redux/messages/messagesSlice";
import {
	usePostMessageMutation,
	usePutMessageMutation,
} from "../../../redux/messages/messagesAPI";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import MessageTableActions from "./MessagesTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
import TableColumnMessageType from "../../components/table/TableColumnMessageType";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { MessageInterface } from "../../../interfaces/Message.interface";
import { MessageTypeInterface } from "../../../interfaces/MessageType.interface";
// CUSTOM HOOKS
import useGetAllMessagesTypes from "../../../customHooks/GET_requests/useGetAllMessagesTypes";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// CONSTANTS
import { BLANK_MESSAGE } from "../../../constants/messages";

function MessageTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Column, ColumnGroup } = Table;
	const { Option } = Select;
	const CONTENT_TYPE: { name: string; value: number }[] = [
		{
			name: "One Hot Message",
			value: 1,
		},
		{ name: "Bit Mask Message", value: 2 },
	];
	const ELEVATOR_CONTROLLER = [
		{ id: 1, name: "Floor Labels" },
		{ id: 2, name: "Firmware Version" },
	];
	const ELEVATOR_STATUS = [
		{ id: 1, name: "Earthquake Protection Message" },
		{ id: 2, name: "Earthquake Status Message" },
		{ id: 3, name: "General Status Message" },
		{ id: 4, name: "Connection Status Message" },
		{ id: 5, name: "Mode Status Message" },
		{ id: 6, name: "Door Status Message" },
		{ id: 7, name: "Traffic Status Message" },
		{ id: 8, name: "Load Weigher Status Message" },
	];
	const ESCALATOR_CONTROLLER = [{ id: 1, name: "Firmware Version Message" }];
	const ESCALATOR_STATUS = [
		{ id: 1, name: "Earthquake Protection Message" },
		{ id: 2, name: "Earthquake Status Message" },
		{ id: 3, name: "General Status Message" },
		{ id: 4, name: "Connection Status Message" },
		{ id: 5, name: "Mode Status Message" },
		{ id: 6, name: "Traffic Status Message" },
		{ id: 7, name: "Load Weigher Status Message" },
	];

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [message, setMessage] = useState<MessageInterface>({
		...BLANK_MESSAGE,
	});
	const [unitType, setUnitType] = useState<"ELEVATOR" | "ESCALATOR">(
		"ELEVATOR",
	);
	const [subTypeList, setSubTypeList] = useState<any>([{ id: 1, name: "NA" }]);
	const [subTypeSelected, setSubTypeSelected] = useState<{
		id: number;
		name: string;
	}>({ id: 1, name: "NA" });
	const [pagination, setPagination] = useState<any>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
	});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { permissions } = useSelector((state: RootState) => state.user);
	const {
		refreshTable,
		formAction,
		message: selectedMessage,
	} = useSelector((state: RootState) => state.message);

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { messagesTypes, isLoadingGetAllMessagesTypes } =
		useGetAllMessagesTypes();
	const {
		messages,
		isLoadingGetAllMessages,
		isErrorGetAllMessages,
		totalRowsGetAllMessages,
		errorMessageGetAllMessages,
	} = useGetAllMessages({ deviceType: "", messageTypeId: "" });

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerPostMessage,
		{
			isSuccess: isSuccessPostMessage,
			isLoading: isLoadingPostMessage,
			isError: isErrorPostMessage,
			error: errorPostMessage,
			reset: resetPostMessage,
		},
	] = usePostMessageMutation();

	const [
		triggerPutMessage,
		{
			isSuccess: isSuccessPutMessage,
			isLoading: isLoadingPutMessage,
			isError: isErrorPutMessage,
			error: errorPutMessage,
			reset: resetPutMessage,
		},
	] = usePutMessageMutation();

	// ************************************************ */
	// HANDLERS *************************************** */
	const getMessageTypeName = (id: number) => {
		if (messagesTypes) {
			const foundType = messagesTypes.find(
				(mt: MessageTypeInterface) => mt.id === id,
			);
			return foundType ? foundType.name : "";
		}
		return "";
	};

	const onChangeFormValue = (value: any, key: string) => {
		const copy = { ...message };
		// @ts-ignore
		copy[key] = value;
		setMessage(copy);
	};

	const onChangeSelectMessageType = (newValue: number) => {
		const copy = structuredClone(message);
		const index = messagesTypes.findIndex(
			(object: MessageTypeInterface) =>
				object.id.toString() === newValue.toString(),
		);

		if (index >= 0) {
			if (
				[
					"CONTROL",
					"CONTROLLERINFO",
					"FAULT",
					"GROUPCONTROL",
					"GROUPSUPERVISORYSTAUS",
					"STATUS",
					"TRAFFIC",
				].includes(messagesTypes[index].name)
			) {
				// "Bit Mask Message"
				copy.message_content_type = 2;
			} else if (["SERVICE", "USAGE"].includes(messagesTypes[index].name)) {
				// "One Hot Message"
				copy.message_content_type = 1;
			}
		}

		copy.message_type_id = newValue;
		setMessage(copy);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		if (formAction === "CREATE") {
			const BODY = {
				token,
				body: message,
			};
			triggerPostMessage(BODY);
		} else {
			const copy = structuredClone(message);
			const BODY = {
				token,
				id: message.id,
				body: copy,
			};
			triggerPutMessage(BODY);
		}
	};

	const resetForm = () => {
		resetPostMessage();
		resetPutMessage();
		setMessage({ ...BLANK_MESSAGE });
		dispatch(updateFormAction("CREATE"));
		dispatch(updateMessage({ ...BLANK_MESSAGE }));
	};

	const checkFormIsValid = () => {
		if (
			message.short_message === "" ||
			message.description === "" ||
			message.enum_id === 0 ||
			message.enum_id === null
		) {
			return true;
		}
		return false;
	};

	const buildCustomSubTypeObject = (
		deviceType: string,
		messageTypeId: number,
		newValue: number,
	) => {
		const copy = structuredClone(message);
		const index = messagesTypes.findIndex(
			(object: MessageTypeInterface) =>
				object.id.toString() === messageTypeId.toString(),
		);
		if (
			index >= 0 &&
			messagesTypes &&
			messagesTypes[index] &&
			messagesTypes[index].name
		) {
			// **********************************************************************************
			// ESCALATOR + CONTROLLER INFO
			if (
				deviceType === "ESCALATOR" &&
				messagesTypes[index].name === "CONTROLLERINFO"
			) {
				setSubTypeList(ESCALATOR_CONTROLLER);
				setSubTypeSelected(ESCALATOR_CONTROLLER[0]);
				//
				copy.elevator_controller_info_message_type = null;
				copy.elevator_status_message_type = null;
				copy.escalator_status_message_type = null;
				//
				copy.escalator_controller_info_message_type = newValue;
			}
			// **********************************************************************************
			// ELEVATOR + CONTROLLER INFO
			else if (
				deviceType === "ELEVATOR" &&
				messagesTypes[index].name === "CONTROLLERINFO"
			) {
				setSubTypeList(ELEVATOR_CONTROLLER);
				setSubTypeSelected(ELEVATOR_CONTROLLER[0]);
				//
				copy.escalator_controller_info_message_type = null;
				copy.escalator_status_message_type = null;
				copy.elevator_status_message_type = null;
				//
				copy.elevator_controller_info_message_type = newValue;
			}
			// **********************************************************************************
			// ESCALATOR + STATUS
			else if (
				deviceType === "ESCALATOR" &&
				messagesTypes[index].name === "STATUS"
			) {
				setSubTypeList(ESCALATOR_STATUS);
				setSubTypeSelected(ESCALATOR_STATUS[0]);
				//
				copy.elevator_controller_info_message_type = null;
				copy.elevator_status_message_type = null;
				copy.escalator_controller_info_message_type = null;
				//
				copy.escalator_status_message_type = newValue;
			}
			// **********************************************************************************
			// ELEVATOR + STATUS
			else if (
				deviceType === "ELEVATOR" &&
				messagesTypes[index].name === "STATUS"
			) {
				setSubTypeList(ELEVATOR_STATUS);
				setSubTypeSelected(ELEVATOR_STATUS[0]);
				//
				copy.escalator_controller_info_message_type = null;
				copy.escalator_status_message_type = null;
				copy.elevator_controller_info_message_type = null;
				//
				copy.elevator_status_message_type = newValue;
			}
			// **********************************************************************************
			else {
				setSubTypeList([{ id: 1, name: "NA" }]);
				setSubTypeSelected({ id: 1, name: "NA" });
				copy.escalator_controller_info_message_type = null;
				copy.escalator_status_message_type = null;
				copy.elevator_controller_info_message_type = null;
				copy.elevator_status_message_type = null;
			}
		}
		setMessage(copy);
	};

	const onChangeSelectSubType = (
		newValue: number,
		device: string,
		messageTypeId: number,
	) => {
		buildCustomSubTypeObject(device, messageTypeId, newValue);
	};

	const getRowSubType = (value: string, record: MessageInterface) => {
		if (record.device_type === "ELEVATOR") {
			if (record.message_type.name === "STATUS") {
				const index = ELEVATOR_STATUS.findIndex(
					(object: { id: number; name: string }) =>
						object.id === record.elevator_status_message_type,
				);
				if (index >= 0) {
					return ELEVATOR_STATUS[index].name;
				}
			}
			if (record.message_type.name === "CONTROLLERINFO") {
				const index = ELEVATOR_CONTROLLER.findIndex(
					(object: { id: number; name: string }) =>
						object.id === record.elevator_controller_info_message_type,
				);
				if (index >= 0) {
					return ELEVATOR_CONTROLLER[index].name;
				}
			}
		}
		if (record.device_type === "ESCALATOR") {
			if (record.message_type.name === "STATUS") {
				const index = ESCALATOR_STATUS.findIndex(
					(object: { id: number; name: string }) =>
						object.id === record.escalator_status_message_type,
				);
				if (index >= 0) {
					return ESCALATOR_STATUS[index].name;
				}
			}
			if (record.message_type.name === "CONTROLLERINFO") {
				const index = ESCALATOR_CONTROLLER.findIndex(
					(object: { id: number; name: string }) =>
						object.id === record.escalator_controller_info_message_type,
				);
				if (index >= 0) {
					return ESCALATOR_CONTROLLER[index].name;
				}
			}
		}
		return "NA";
	};

	const getRowSubTypeId = (record: MessageInterface) => {
		if (record.elevator_controller_info_message_type) {
			return record.elevator_controller_info_message_type;
		}
		if (record.elevator_status_message_type) {
			return record.elevator_status_message_type;
		}
		if (record.escalator_controller_info_message_type) {
			return record.escalator_controller_info_message_type;
		}
		if (record.escalator_status_message_type) {
			return record.escalator_status_message_type;
		}
		return 0;
	};

	const handleTableChange = async (pagination_: any) => {
		setPagination({
			...pagination,
			current: pagination_.current,
			pageSize: pagination_.pageSize,
			total: pagination_.total,
		});
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (refreshTable) {
			resetForm();
		}
	}, [refreshTable]);

	useEffect(() => {
		resetForm();
	}, [
		isSuccessPostMessage,
		isSuccessPutMessage,
		isErrorPostMessage,
		isErrorPutMessage,
	]);

	useEffect(() => {
		if (formAction === "CREATE") {
			setMessage({ ...BLANK_MESSAGE });
		}
	}, [formAction]);

	useEffect(() => {
		setMessage(selectedMessage);
	}, [selectedMessage]);

	useEffect(() => {
		buildCustomSubTypeObject(message.device_type, message.message_type_id, 1);
	}, [message.device_type, message.message_type_id]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<div>
				{validatePermission("permission-not-defined", permissions) ? (
					<>
						<Table
							locale={{
								emptyText: (
									<TableNoDataBox
										isError={isErrorGetAllMessages}
										errorMessage={errorMessageGetAllMessages}
										noItemsFound='No actions found'
									/>
								),
							}}
							rowClassName={(record, index) => getTableRowClass(index, theme)}
							dataSource={messages}
							pagination={{ ...pagination, total: totalRowsGetAllMessages }}
							onChange={handleTableChange}
							size='small'
							className='my-custom-table'
							loading={
								isLoadingGetAllMessages ||
								isLoadingGetAllMessagesTypes ||
								isLoadingPostMessage ||
								isLoadingPutMessage
							}
							footer={() => (
								<div className='generalStyles__flexEnd'>
									<Tag>{totalRowsGetAllMessages} Rows</Tag>
								</div>
							)}
						>
							{/** ID */}
							<ColumnGroup title='ID'>
								<Column
									title='ID'
									dataIndex='id'
									key='id'
									render={(text: string) => (
										<div>
											<span>{text}</span>
										</div>
									)}
								/>
							</ColumnGroup>
							{/** MESSAGE  TYPES */}
							<ColumnGroup title='Message Type'>
								<Column
									title={
										<Select
											className='generalStyles__width100'
											onChange={(value) => {
												onChangeSelectMessageType(value);
											}}
											disabled={messagesTypes.length === 0}
											showSearch
											size='small'
											loading={isLoadingGetAllMessagesTypes}
											value={message.message_type_id}
											optionFilterProp='children'
											filterOption={(input: string, option: any) =>
												option.children
													.toLowerCase()
													.includes(input.toLowerCase())
											}
										>
											{messagesTypes.map((item: MessageTypeInterface) => (
												<Option key={item.id} value={item.id}>
													{item.name}
												</Option>
											))}
										</Select>
									}
									dataIndex='message_type_id'
									key='message_type_id'
									render={(text: number, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<Select
													className='generalStyles__width100'
													onChange={(value) => {
														onChangeSelectMessageType(value);
													}}
													loading={isLoadingGetAllMessagesTypes}
													disabled
													showSearch
													size='small'
													value={message.message_type_id}
													optionFilterProp='children'
													filterOption={(input: string, option: any) =>
														option.children
															.toLowerCase()
															.includes(input.toLowerCase())
													}
												>
													{messagesTypes.map((item: MessageTypeInterface) => (
														<Option key={item.id} value={item.id}>
															{item.name}
														</Option>
													))}
												</Select>
											) : (
												<TableColumnMessageType
													messageType={getMessageTypeName(text)}
												/>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** DEVICE TYPE */}
							<ColumnGroup title='Device Type'>
								<Column
									title={
										<Select
											className='generalStyles__width100'
											onChange={(value) => {
												setUnitType(value);
												onChangeFormValue(value, "device_type");
											}}
											disabled={formAction === "EDIT"}
											showSearch
											size='small'
											value={unitType}
											optionFilterProp='children'
										>
											{["ELEVATOR", "ESCALATOR"].map((item: string) => (
												<Option key={item} value={item}>
													{item}
												</Option>
											))}
										</Select>
									}
									dataIndex='device_type'
									key='device_type'
									render={(text: string, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<Select
													className='generalStyles__width100'
													onChange={(value) => {
														setUnitType(value);
														onChangeFormValue(value, "device_type");
													}}
													disabled={formAction === "EDIT"}
													showSearch
													size='small'
													value={unitType}
													optionFilterProp='children'
												>
													{["ELEVATOR", "ESCALATOR"].map((item: string) => (
														<Option key={item} value={item}>
															{item}
														</Option>
													))}
												</Select>
											) : (
												<div>
													{text === "ELEVATOR" && (
														<Tag
															style={{ width: "125px", textAlign: "center" }}
															color='blue'
														>
															{text}
														</Tag>
													)}
													{text === "ESCALATOR" && (
														<Tag
															style={{ width: "125px", textAlign: "center" }}
															color='gold'
														>
															{text}
														</Tag>
													)}
												</div>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** MESSAGE SUB TYPE */}
							<ColumnGroup title='Message Sub Type'>
								<Column
									title={
										<Select
											className='generalStyles__width100'
											onChange={(value) => {
												onChangeSelectSubType(
													value,
													message.device_type,
													message.message_type_id,
												);
											}}
											disabled={
												formAction === "EDIT" || subTypeSelected.name === "NA"
											}
											showSearch
											size='small'
											value={getRowSubTypeId(message)}
											optionFilterProp='children'
										>
											{subTypeList.map((item: { id: number; name: string }) => (
												<Option key={item.id} value={item.id}>
													{item.name}
												</Option>
											))}
										</Select>
									}
									dataIndex='message_sub_type'
									key='message_sub_type'
									render={(text: string, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<Select
													className='generalStyles__width100'
													onChange={(value) => {
														onChangeSelectSubType(
															value,
															record.device_type,
															record.message_type_id,
														);
													}}
													disabled={
														formAction === "EDIT" ||
														subTypeSelected.name === "NA"
													}
													showSearch
													size='small'
													value={subTypeSelected.id}
													optionFilterProp='children'
												>
													{subTypeList.map(
														(item: { id: number; name: string }) => (
															<Option key={item.id} value={item.id}>
																{item.name}
															</Option>
														),
													)}
												</Select>
											) : (
												<Input
													size='small'
													disabled
													value={getRowSubType(text, record)}
												/>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** CONTENT TYPE */}
							<ColumnGroup title='Content Type'>
								<Column
									title={
										<Select
											className='generalStyles__width100'
											onChange={(value) => {
												onChangeFormValue(value, "message_content_type");
											}}
											disabled={formAction === "EDIT"}
											showSearch
											size='small'
											value={message.message_content_type}
											optionFilterProp='children'
										>
											{CONTENT_TYPE.map(
												(item: { name: string; value: number }) => (
													<Option key={item.value} value={item.value}>
														{item.name}
													</Option>
												),
											)}
										</Select>
									}
									dataIndex='message_content_type'
									key='message_content_type'
									render={(text: number, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<Select
													className='generalStyles__width100'
													onChange={(value) => {
														onChangeFormValue(value, "message_content_type");
													}}
													showSearch
													size='small'
													value={message.message_content_type}
													optionFilterProp='children'
												>
													{CONTENT_TYPE.map(
														(item: { name: string; value: number }) => (
															<Option key={item.value} value={item.value}>
																{item.name}
															</Option>
														),
													)}
												</Select>
											) : (
												<div>
													{text === 1 && (
														<Tag
															style={{ width: "150px", textAlign: "center" }}
															color='blue'
														>
															One hot Mask Message
														</Tag>
													)}
													{text === 2 && (
														<Tag
															style={{ width: "150px", textAlign: "center" }}
															color='gold'
														>
															Bit Mask Message
														</Tag>
													)}
												</div>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** ENUM ID */}
							<ColumnGroup title='Enum ID'>
								<Column
									width={120}
									title={
										<InputNumber
											style={{ width: "100%" }}
											size='small'
											placeholder='Enum ID'
											min={0}
											value={formAction === "CREATE" ? message.enum_id : null}
											onChange={(e: any) => {
												onChangeFormValue(e, "enum_id");
											}}
										/>
									}
									dataIndex='enum_id'
									key='enum_id'
									render={(text, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<InputNumber
													style={{ width: "100%" }}
													size='small'
													placeholder='Enum ID'
													min={0}
													value={message.enum_id}
													onChange={(e: any) => {
														onChangeFormValue(e, "enum_id");
													}}
												/>
											) : (
												<div>{text}</div>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** SHORT MESSAGE */}
							<ColumnGroup title='Short Message'>
								<Column
									width={200}
									title={
										<Input
											size='small'
											placeholder='Short Message'
											value={
												formAction === "CREATE" ? message.short_message : ""
											}
											disabled={formAction === "EDIT"}
											onChange={(event: any) => {
												onChangeFormValue(
													event.currentTarget.value,
													"short_message",
												);
											}}
										/>
									}
									dataIndex='short_message'
									key='short_message'
									render={(text, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<Input
													size='small'
													placeholder='Short Message'
													value={message.short_message}
													onChange={(event: any) => {
														onChangeFormValue(
															event.currentTarget.value,
															"short_message",
														);
													}}
												/>
											) : (
												<>{text}</>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** DESCRIPTION */}
							<ColumnGroup title='Description'>
								<Column
									width={200}
									title={
										<Input
											size='small'
											placeholder='Description'
											value={
												formAction === "CREATE" ? message.description : "--"
											}
											disabled={formAction === "EDIT"}
											onChange={(event: any) => {
												onChangeFormValue(
													event.currentTarget.value,
													"description",
												);
											}}
										/>
									}
									dataIndex='description'
									key='description'
									render={(text, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<Input
													size='small'
													placeholder='Description'
													value={message.description}
													onChange={(event: any) => {
														onChangeFormValue(
															event.currentTarget.value,
															"description",
														);
													}}
												/>
											) : (
												<div>{text || "--"}</div>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** POSITION */}
							<ColumnGroup title='Position'>
								<Column
									title={
										<Switch
											onChange={(e) => {
												onChangeFormValue(e, "position");
											}}
											disabled={formAction === "EDIT"}
											checkedChildren={t("configuration.message.yes")}
											unCheckedChildren={t("configuration.message.no")}
											checked={formAction === "EDIT" ? false : message.position}
										/>
									}
									dataIndex='position'
									key='position'
									render={(text: boolean, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<Switch
													onChange={(e) => {
														onChangeFormValue(e, "position");
													}}
													checkedChildren={t("configuration.message.yes")}
													unCheckedChildren={t("configuration.message.no")}
													checked={message.position}
												/>
											) : (
												<>
													<Switch
														disabled
														onChange={(e) => {
															onChangeFormValue(e, "position");
														}}
														checkedChildren={t("configuration.message.yes")}
														unCheckedChildren={t("configuration.message.no")}
														checked={text}
													/>
												</>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** ACTIONS */}
							<ColumnGroup
								align='center'
								title={t("edgeManagement.banks.actions")}
								width={200}
							>
								<Column
									align='center'
									width={200}
									title={
										<div className='generalStyles__flexCenter'>
											<div>
												<Tooltip title={t("general.save")}>
													<Button
														className={
															formAction === "EDIT" || checkFormIsValid()
																? " buttonStyle__inactive__1__dark"
																: "buttonStyle__10"
														}
														disabled={
															checkFormIsValid() || formAction === "EDIT"
														}
														onClick={onClickSave}
														icon={<FontAwesomeIcon icon={faSave} />}
													/>
												</Tooltip>
											</div>
											<div className='generalStyles__mlFix'>
												<Tooltip title={t("general.clear")}>
													<Button
														className={
															formAction === "CREATE" ? "buttonStyle__14" : ""
														}
														onClick={resetForm}
														disabled={formAction === "EDIT"}
														icon={<FontAwesomeIcon icon={faX} />}
													/>
												</Tooltip>
											</div>
										</div>
									}
									key='action'
									render={(_: any, record: MessageInterface) => (
										<div>
											{formAction === "EDIT" && record.id === message.id ? (
												<div className='generalStyles__flexCenter'>
													<div>
														<Tooltip title={t("general.save")}>
															<Button
																className='buttonStyle__10'
																disabled={checkFormIsValid()}
																onClick={onClickSave}
																icon={<FontAwesomeIcon icon={faSave} />}
															/>
														</Tooltip>
													</div>
													<div className='generalStyles__mlFix'>
														<Tooltip title={t("general.cancel")}>
															<Button
																className='buttonStyle__14'
																onClick={resetForm}
																icon={<FontAwesomeIcon icon={faX} />}
															/>
														</Tooltip>
													</div>
												</div>
											) : (
												<MessageTableActions selectedItem={record} />
											)}
										</div>
									)}
								/>
							</ColumnGroup>
						</Table>
					</>
				) : (
					<>
						<div className='generalStyles__noAccessToListTable'>
							<Empty
								description={t(
									"organizationManagement.listNotAvailableOrganizations",
								)}
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						</div>
					</>
				)}
			</div>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostMessage}
				isSuccess={isSuccessPostMessage}
				requestType='POST'
				error={errorPostMessage}
				name='Message Type'
			/>
			<GlobalAlert2
				isError={isErrorPutMessage}
				isSuccess={isSuccessPutMessage}
				requestType='PUT'
				error={errorPutMessage}
				name='Message Type'
			/>
		</div>
	);
}

export default MessageTable;
